/* -------------------------------------------------------------------------- */
/*  FITE – Accounting Export (CSV export)                                     */
/* -------------------------------------------------------------------------- *
 *  - Cost codes from application-level "Cost Code" sort field               *
 *  - Cost categories from application-level "Cost Category" sort field      *
 *  - L, M, S breakouts applied to corresponding Labor, Material, Sub cats   *
 *  - All other categories use the Cost Type sort field designation          *
 *  - Assembly children are exported in place of their parent items           *
 *  - Returns [stringLines, "csv"]                                            */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../errors";
import { CategoryMap, CategoryKey, nonZeroCategories } from "./categories";
import { BudgetImportConfig, BudgetRow } from "./framework/types";
import { csvEscape, truncateList } from "./framework/helpers";

/* ───────────────────────── 1. Constants ─────────────────────────────────── */

// Cost category mapping for LMSEO breakdown
// L, M, S are automatically mapped; ER, O, G, P must be designated via Cost Type field
const CATEGORY_MAP: CategoryMap = {
  labor: "L", // Labor
  material: "M", // Material
  equip: "ER", // Equipment Rental
  sub: "S", // Subcontracts
  other: "O", // Other
  total: "S", // Fallback when all LMSEO = 0
};

/** Fite's CSV carries a description beside each category code. */
const CATEGORY_DESCRIPTIONS: Record<CategoryKey, string> = {
  labor: "Labor",
  material: "Material",
  equip: "Equipment",
  sub: "Subcontract",
  other: "Other",
  total: "Subcontract",
};

/* ───────────────────────── 2. Helpers ───────────────────────────────────── */

/**
 * Validate that all line items have required Cost Code field
 * Returns array of item names missing cost codes
 */
const validateCostCodes = (rows: BudgetRow[]): string[] => {
  const missing: string[] = [];
  for (const row of rows) {
    if (row.type === "line_item" && !row["Cost Code_code"]) {
      missing.push(row.name || "(unnamed item)");
    }
  }
  return missing;
};

const identifyLMSEOCategories = (row: BudgetRow): Array<[string, string, number]> =>
  nonZeroCategories(row).map(({ key, unitCost }) => [CATEGORY_MAP[key], CATEGORY_DESCRIPTIONS[key], unitCost]);

/* ───────────────────────── 3. Config ────────────────────────────────────── */

export const fiteConfig: BudgetImportConfig = {
  format: "sage",
  requires: { floorAreas: true, project: true, assemblyChildren: true },
  sage: {
    ext: "csv",
    render: (ctx) => {
      const estimateRows = ctx.rows;
      const assemblyItemRows = ctx.assemblyChildren;
      const project = ctx.project!;

      /* Validate cost codes (Cost Code_code required for all line items) */
      const missingCostCodes = validateCostCodes(estimateRows);
      if (missingCostCodes.length > 0) {
        throw new ValidationError(`Missing Cost Code for items: ${truncateList(missingCostCodes)}`);
      }

      /* File header line */
      const lines: string[] = [";Precision Estimating Interface File,,,,,,,,,,,,,"];

      /* Project header ("*") */
      const totalArea = ctx.totalArea;

      // Parse address components
      const addr = (project.address ?? "").split(", ");
      //const addr1 = addr[0] ?? "";
      const city = project.city ?? addr[1] ?? "";
      const state = project.state ?? (addr[2] ?? "").split(" ")[0] ?? "";
      const client = project.client_name ?? "";

      // Header format: *,<job_num>,<name>,,<city>,<state>,,<name>,<address>,<city>,<state>,,<sqft>,<unit>
      const header = [
        "*",
        "", //placeholder for sage job number
        csvEscape(project.name),
        "",
        "",
        "",
        "",
        csvEscape(client),
        "",
        csvEscape(city),
        csvEscape(state),
        "",
        totalArea,
        "Sqft",
      ].join(",");

      lines.push(header);

      /* Cost detail records ("P" and "C") */
      const dateNum = ctx.dateNum; // MMDDYYYY format

      // fite request assembly children instead of parent items in export
      const assemblyParentIds = new Set<string>();
      for (const row of assemblyItemRows) {
        if (row["parent"]) assemblyParentIds.add(row["parent"]);
      }

      /* Assembly Child Items */
      for (const row of assemblyItemRows) {
        const costCode = row["Cost Code_code"];
        const description = row["Cost Code_desc"] || "";

        // P record for assembly child item
        const pRecord = [
          "P",
          csvEscape(costCode),
          csvEscape(description),
          "",
          dateNum,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ].join(",");
        lines.push(pRecord);

        // C record for assembly child item total
        const categories = identifyLMSEOCategories(row);
        for (const [categoryCode, categoryDesc, unitCost] of categories) {
          const amount = parseFloat((row.quantity * unitCost).toFixed(2));
          const cRecord = [
            "C",
            csvEscape(costCode),
            csvEscape(categoryDesc), // Category description
            csvEscape(categoryCode), // Category code
            dateNum,
            "",
            "",
            amount,
            "",
            "",
            "",
            "",
            "",
            "",
          ].join(",");
          lines.push(cRecord);
        }
      }

      for (const row of estimateRows) {
        /* Line Items */
        if (row.type === "line_item" && !assemblyParentIds.has(row["id"])) {
          const costCode = row["Cost Code_code"];
          const description = row["Cost Code_desc"] || "";

          // P record: phase/cost code line
          // Format: P,<cost_code>,<description>,,<date>,,,,,,,,,
          const pRecord = [
            "P",
            csvEscape(costCode),
            csvEscape(description),
            "",
            dateNum,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ].join(",");
          lines.push(pRecord);

          // C records: one per LMSEO category with non-zero cost
          const categories = identifyLMSEOCategories(row);
          for (const [categoryCode, categoryDesc, unitCost] of categories) {
            const amount = parseFloat((row.quantity * unitCost).toFixed(2));

            // C record: cost detail
            // Format: C,<cost_code>,<category_name>,<category_code>,<date>,,,<amount>,,,,,,
            const cRecord = [
              "C",
              csvEscape(costCode),
              csvEscape(categoryDesc), // Category description
              csvEscape(categoryCode), // Category code
              dateNum,
              "",
              "",
              amount,
              "",
              "",
              "",
              "",
              "",
            ].join(",");
            lines.push(cRecord);
          }
        }

        /* Markups */
        if (row.type === "markup" || row.type === "atl_markup") {
          const costCode = row["Cost Code_code"] || "20-600"; // Default to G&A code
          const description = row["Cost Code_desc"] || "";
          const category = row["Cost Category_code"] || "G"; // Default to General Overhead
          const categoryDesc = row["Cost Category_desc"] || "General Overhead";

          // P record for markup
          const pRecord = [
            "P",
            csvEscape(costCode),
            csvEscape(description),
            "",
            dateNum,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ].join(",");
          lines.push(pRecord);

          // C record for markup total
          const cRecord = [
            "C",
            csvEscape(costCode),
            csvEscape(categoryDesc),
            csvEscape(category),
            dateNum,
            "",
            "",
            parseFloat(Number(row.total).toFixed(2)),
            "",
            "",
            "",
            "",
            "",
          ].join(",");
          lines.push(cRecord);
        }
      }

      return lines;
    },
  },
};
